import { SubmitButton } from "@ameelio/ui";
import { useLazyQuery } from "@apollo/client/react";
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { endOfQuarter, startOfQuarter } from "date-fns";
import { useState } from "react";
import { CardSkeleton } from "../lib/closet";
import { DateInputBase } from "../lib/DateInput";
import meetingTypeLabel from "../lib/meetingTypeLabel";
import Screen from "../lib/Screen";
import { GetActiveAccountsReportDocument } from "./GetActiveAccountsReport.generated";
import { Widget, WidgetRow, WidgetRowLayout } from "./Widget";

export default function AccountsReportScreen() {
  const [startDate, setStartDate] = useState<Date>(startOfQuarter(new Date()));
  const [endDate, setEndDate] = useState<Date>(endOfQuarter(new Date()));

  const [
    fetchActivityReport,
    { data: activityData, error: activityError, loading: activityLoading },
  ] = useLazyQuery(GetActiveAccountsReportDocument, {
    fetchPolicy: "cache-first", // in the unlikely event someone searches the same date range
  });

  if (activityError) throw activityError;

  return (
    <Screen title="Active users" maxWidth="md">
      <Stack spacing={2}>
        <Stack spacing={2} direction="row">
          <DateInputBase
            label="Start"
            value={new Date(startDate)}
            onChange={(val) => {
              if (val) setStartDate(val);
            }}
          />
          <DateInputBase
            label="End"
            value={new Date(endDate)}
            onChange={(val) => {
              if (val) setEndDate(val);
            }}
          />
          <SubmitButton
            submitting={false}
            onClick={() => {
              fetchActivityReport({
                variables: {
                  startDate: startDate.toISOString().split("T")[0],
                  endDate: endDate.toISOString().split("T")[0],
                },
              });
            }}
          >
            Go
          </SubmitButton>
        </Stack>

        {activityData ? (
          <>
            <WidgetRow layout={WidgetRowLayout.HALF}>
              <Widget
                title="Active residents"
                value={activityData.reports.activeInmates}
              />
              <Widget title="Residents breakdown">
                <Table size="small">
                  <TableHead>
                    <TableCell>Type</TableCell>
                    <TableCell>People</TableCell>
                    <TableCell>Hours</TableCell>
                  </TableHead>
                  <TableBody>
                    {activityData.reports.activeInmatesByMeetingType.map(
                      (d) => (
                        <TableRow key={d.meetingType}>
                          <TableCell>
                            {meetingTypeLabel(d.meetingType)}
                          </TableCell>
                          <TableCell>{d.count}</TableCell>
                          <TableCell>{d.hours}</TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </Widget>
            </WidgetRow>
            <WidgetRow layout={WidgetRowLayout.HALF}>
              <Widget
                title="Active visitors"
                value={activityData.reports.activeStandardVisitors}
              />
              <Widget title="Visitors breakdown">
                <Table size="small">
                  <TableHead>
                    <TableCell>Type</TableCell>
                    <TableCell>People</TableCell>
                    <TableCell>Hours</TableCell>
                  </TableHead>
                  <TableBody>
                    {activityData.reports.activeStandardVisitorsByMeetingType.map(
                      (d) => (
                        <TableRow key={d.meetingType}>
                          <TableCell>
                            {meetingTypeLabel(d.meetingType)}
                          </TableCell>
                          <TableCell>{d.count}</TableCell>
                          <TableCell>{d.hours}</TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </Widget>
            </WidgetRow>
            <WidgetRow layout={WidgetRowLayout.HALF}>
              <Widget
                title="Active professionals"
                value={activityData.reports.activeProfessionalVisitors}
              />
              <Widget title="Professionals breakdown">
                <Table size="small">
                  <TableHead>
                    <TableCell>Type</TableCell>
                    <TableCell>People</TableCell>
                    <TableCell>Hours</TableCell>
                  </TableHead>
                  <TableBody>
                    {activityData.reports.activeProfessionalVisitorsByMeetingType.map(
                      (d) => (
                        <TableRow key={d.meetingType}>
                          <TableCell>
                            {meetingTypeLabel(d.meetingType)}
                          </TableCell>
                          <TableCell>{d.count}</TableCell>
                          <TableCell>{d.hours}</TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </Widget>
            </WidgetRow>
          </>
        ) : activityLoading ? (
          <WidgetRow layout={WidgetRowLayout.HALF}>
            <CardSkeleton height={200} />
            <CardSkeleton height={200} />
          </WidgetRow>
        ) : null}
      </Stack>
    </Screen>
  );
}
